import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Tooltip } from 'common/components/Tooltip/Tooltip';

import CombinedIcon from 'assets/images/combined.svg';
import ResetWidthIcon from 'assets/images/resetWidth.svg';
import { useTranslation } from 'react-i18next';
import { useDrag, useDrop } from 'react-dnd';
import { CheckboxStyled } from 'common/components/CheckboxStyled/CheckboxStyled';
import { palette } from 'theme/palette';
import {
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';
import {
  TABLE_VARIANT,
  ColumnConfiguration,
  TableVariant,
} from '../../Sim/Inventory/constants/columnConfigurationsConstants';
import { pxToRem } from 'src/common/utils/fonts';
import { useDnDRef } from 'src/common/hooks/useDnDRef';

type TableSettingsItemProps = {
  column: ColumnConfiguration;

  handleWidthChange: (columnName: string, width: 'auto', isCustomField: boolean) => void;
  handleDrop: (oldIndex: number, newIndex: number) => void;
  variant: TableVariant;
  idLabelMap: Record<string, string>;
  index: number;
};

const NameTypoStyled = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: `calc(100% - ${theme.spacing(45)})`,
}));

const NameBoxStyled = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
});

export const TableSettingsItem: React.FC<TableSettingsItemProps> = ({
  column,
  index,
  handleWidthChange,
  handleDrop,
  variant,
  idLabelMap,
}) => {
  const { t } = useTranslation();

  const [_, dragSource, dragPreview] = useDrag(
    () => ({
      type: 'column',
      item: { columnName: column.name, columnIndex: index },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [column.name],
  );

  const [{ isOver, differenceFromInitialOffset }, dropTarget] = useDrop({
    accept: 'column',
    drop: (item: any) => {
      handleDrop(item.columnIndex, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset(),
    }),
  });

  const dropTargetRef = useDnDRef(dropTarget);
  const dragPreviewRef = useDnDRef(dragPreview);
  const dragSourceRef = useDnDRef(dragSource);

  const ref = useGridApiContext();

  const visibilityModel = useGridSelector(ref, gridColumnVisibilityModelSelector);

  return (
    <Box ref={dropTargetRef}>
      <Box ref={dragPreviewRef}>
        <Box
          ref={dragSourceRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: `${pxToRem(24)} auto`,
            gridAutoRows: `minmax(${pxToRem(26)}, ${pxToRem(28)})`,
            alignItems: 'center',
            columnGap: 4,
            rowGap: '0',
            '&:hover': {
              backgroundColor: palette.grey[100],
            },
            cursor: 'move',
            borderBottom: `2px solid ${
              isOver && differenceFromInitialOffset?.y && differenceFromInitialOffset.y > 0
                ? palette.grey[600]
                : palette.text.white
            }`,
            borderTop: `2px solid ${
              isOver && differenceFromInitialOffset?.y && differenceFromInitialOffset.y < 0
                ? palette.grey[600]
                : palette.text.white
            }`,
          }}
        >
          <CheckboxStyled
            isChecked={visibilityModel[column.name]}
            onChange={(isChecked) => {
              const test = async () => {
                ref.current.setColumnVisibility(column.name, isChecked);
              };
              test();
            }}
            testId={`configurable-table-settings-checkbox-${column.name}`}
          />
          <NameBoxStyled>
            {column.isCustomField ? (
              <Tooltip title={column.name}>
                <NameTypoStyled variant="text1">{column.name}</NameTypoStyled>
              </Tooltip>
            ) : (
              <NameTypoStyled variant="text1">{idLabelMap[column.name]}</NameTypoStyled>
            )}
            {column.width === 'auto' ? null : (
              <Tooltip title={t('common.resetColumnWidth')}>
                <ResetWidthIcon
                  style={{ marginLeft: '0.625rem', cursor: 'pointer' }}
                  width={pxToRem(15)}
                  height={pxToRem(15)}
                  role="img"
                  data-testid={`reset-width-${column.name}`}
                  onClick={() => handleWidthChange(column.name, 'auto', column.isCustomField)}
                />
              </Tooltip>
            )}
          </NameBoxStyled>
          {variant === TABLE_VARIANT.Combined && column.children?.length > 0 ? (
            <>
              <div style={{ height: pxToRem(28) }}>
                <CombinedIcon
                  style={{ marginLeft: '0.6875rem' }}
                  width={pxToRem(15)}
                  height={pxToRem(17)}
                  role="img"
                  data-testid={`combined-icon-${column.name}`}
                />
              </div>
              <Typography
                variant="text1"
                color={column.enabled ? 'text.primary' : 'text.secondary'}
                sx={{
                  ml: 8,
                  height: pxToRem(28),
                  textTransform: 'capitalize',
                }}
              >
                {idLabelMap[column.children[0]?.name]}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
